import type { Setting } from "@skyware-group/iris-api-model";

import { BaseClient } from "../../clients/base/BaseClient";
import type { Paged } from "../_shared/pagination.types";
import { SETTINGS_ENDPOINTS } from "./settings.consts";
import type { SettingsQueryParams, SettingUpsertRequest } from "./settings.types";

/**
 * Settings API Client
 * All operations are admin-only.
 */
export class SettingsClient extends BaseClient {
  /**
   * Fetch a page of settings.
   * @param params - Optional pagination parameters.
   * @param signal - Optional abort signal.
   */
  async fetchSettings(params?: SettingsQueryParams, signal?: AbortSignal | null): Promise<Paged<Setting>> {
    return this.get<Paged<Setting>>(SETTINGS_ENDPOINTS.SETTINGS, {
      params: params as Record<string, unknown> | undefined,
      signal,
    });
  }

  /**
   * Fetch a single setting by key.
   * @param key - Setting key.
   * @param signal - Optional abort signal.
   */
  async fetchSettingByKey(key: string, signal?: AbortSignal | null): Promise<Setting> {
    return this.get<Setting>(SETTINGS_ENDPOINTS.SETTING_BY_KEY(key), { signal });
  }

  /**
   * Upsert (create or update) a setting by natural key.
   * @param key - Setting key.
   * @param data - Upsert request body.
   * @param signal - Optional abort signal.
   */
  async upsertSetting(key: string, data: SettingUpsertRequest, signal?: AbortSignal | null): Promise<Setting> {
    return this.put<Setting>(SETTINGS_ENDPOINTS.UPSERT_SETTING(key), { body: data, signal });
  }

  /**
   * Delete a setting by natural key.
   * @param key - Setting key.
   * @param signal - Optional abort signal.
   */
  async deleteSetting(key: string, signal?: AbortSignal | null): Promise<void> {
    return this.delete<never>(SETTINGS_ENDPOINTS.DELETE_SETTING(key), { signal });
  }
}
